import Box from '@mui/material/Box';
import { keyframes } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';

import { COLORS } from '../../../configs/colors';
import { parseSLASummaryString } from '../../molecules/SLACircleIndicator';

const flash = keyframes`0%,100%{opacity:1}50%{opacity:0.3}`;

const formatRemaining = (hours: number): string => {
  if (hours < 0) return 'Breached';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${Math.floor(hours)}h ${Math.round((hours % 1) * 60)}m`;
  const days = Math.floor(hours / 24);
  const h = Math.floor(hours % 24);
  return `${days}d ${h}h`;
};

interface Urgency { color: string; label: string; flash: boolean }

const getUrgency = (hoursRemaining: number, breached: boolean): Urgency => {
  if (breached) return { color: COLORS.SLAUrgency0, label: 'Breached', flash: false };
  if (hoursRemaining < 0) return { color: COLORS.SLAUrgency1, label: 'Breached', flash: true };
  if (hoursRemaining < 4) return { color: COLORS.SLAUrgency1, label: formatRemaining(hoursRemaining), flash: true };
  if (hoursRemaining < 8) return { color: COLORS.SLAUrgency2, label: formatRemaining(hoursRemaining), flash: false };
  if (hoursRemaining < 16) return { color: COLORS.SLAUrgency3, label: formatRemaining(hoursRemaining), flash: false };
  if (hoursRemaining < 24) return { color: COLORS.SLAUrgency4, label: formatRemaining(hoursRemaining), flash: false };
  if (hoursRemaining < 48) return { color: COLORS.SLAUrgency5, label: formatRemaining(hoursRemaining), flash: false };
  return { color: COLORS.SLAUrgency6, label: formatRemaining(hoursRemaining), flash: false };
};

export interface SlaCellProps {
  value: unknown;
}

/** Compact SLA burndown bar for table cells. Parses the JSON summary string. */
export const SlaCell: React.FC<SlaCellProps> = ({ value }) => {
  if (value == null || value === '') return <>—</>;
  const raw = typeof value === 'string' ? value : JSON.stringify(value);
  const sla = parseSLASummaryString(raw);
  if (!sla?.timeoutAt) return <>—</>;

  const timeoutMs = new Date(sla.timeoutAt).getTime();
  const remainingMs = timeoutMs - Date.now();
  const hoursRemaining = remainingMs / (1000 * 60 * 60);
  const breached = sla.slaOutcome?.outcomeTypeCode === 'breached';

  const totalWindow = 48;
  const elapsed = totalWindow - Math.max(0, hoursRemaining);
  const progress = Math.min(100, Math.max(0, (elapsed / totalWindow) * 100));
  const urgency = getUrgency(hoursRemaining, breached);

  return (
    <Tooltip
      title={`${urgency.label} — expires ${new Date(sla.timeoutAt).toLocaleString()}`}
      placement="top"
      arrow
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 90 }}>
        <Box
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'v4.filterInput', overflow: 'hidden' }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${progress}%`,
              bgcolor: urgency.color,
              borderRadius: 2,
              ...(urgency.flash ? { animation: `${flash} 1.5s ease-in-out infinite` } : {}),
            }}
          />
        </Box>
        <Typography
          sx={{
            fontSize: '10px',
            fontWeight: 600,
            color: urgency.color,
            whiteSpace: 'nowrap',
            lineHeight: 1,
            ...(urgency.flash ? { animation: `${flash} 1.5s ease-in-out infinite` } : {}),
          }}
        >
          {urgency.label}
        </Typography>
      </Box>
    </Tooltip>
  );
};
